/**
 * IdpConfigList - IDP Configuration Management
 * Lists, creates, edits, and deletes IDP configurations
 */

import React, { useState, useEffect } from 'react';
import {
  listIdpConfigurations,
  createIdpViaDiscovery,
  createIdpManual,
  updateIdpConfiguration,
  deleteIdpConfiguration,
  uploadPublicKey,
  removePublicKey,
  getIdpApplications,
} from './idpApi';
import JitConfigForm from './JitConfigForm';

const PROVIDER_ICONS = {
  auth0: { icon: 'ti-id-badge', color: 'c-orange-500' },
  okta: { icon: 'ti-shield', color: 'c-blue-500' },
  azure_ad: { icon: 'ti-cloud', color: 'c-teal-500' },
  generic_oidc: { icon: 'ti-key', color: 'c-grey-500' },
};

const IdpConfigList = ({ orgId }) => {
  const [idps, setIdps] = useState([]);
  const [appCounts, setAppCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [editingIdp, setEditingIdp] = useState(null);
  const [keyManagerIdp, setKeyManagerIdp] = useState(null);

  useEffect(() => {
    loadIdps();
  }, [orgId, showInactive]);

  const loadIdps = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await listIdpConfigurations(orgId, !showInactive);
      const idpList = Array.isArray(data) ? data : [];
      setIdps(idpList);

      // Fetch app counts for each IDP
      const counts = {};
      await Promise.all(
        idpList.map(async (idp) => {
          try {
            const apps = await getIdpApplications(orgId, idp.id);
            counts[idp.id] = Array.isArray(apps) ? apps.length : 0;
          } catch {
            counts[idp.id] = 0;
          }
        })
      );
      setAppCounts(counts);
    } catch (e) {
      console.error('Failed to load IDPs:', e);
      setError('Failed to load identity providers');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (idp) => {
    if (!window.confirm(`Are you sure you want to deactivate "${idp.name}"? Applications using this IDP will no longer be able to verify tokens.`)) {
      return;
    }

    try {
      await deleteIdpConfiguration(orgId, idp.id);
      setSuccess(`"${idp.name}" has been deactivated`);
      setTimeout(() => setSuccess(''), 3000);
      loadIdps();
    } catch (e) {
      console.error('Failed to delete IDP:', e);
      setError('Failed to deactivate identity provider');
    }
  };

  const getProviderStyle = (type) => {
    return PROVIDER_ICONS[type] || PROVIDER_ICONS.generic_oidc;
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="idp-config-list">
      <div className="d-flex justify-content-between align-items-center mB-20">
        <h5 className="mB-0">Identity Providers</h5>
        <div className="d-flex gap-3 align-items-center">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="showInactive"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="showInactive">
              Show inactive
            </label>
          </div>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <i className="ti-plus mR-5"></i>
            Add Provider
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      {idps.length === 0 ? (
        <div className="text-center p-4 bgc-grey-100 bdrs-4">
          <i className="ti-id-badge fsz-xl c-grey-400 mB-10 d-block"></i>
          <p className="mB-10">No identity providers configured.</p>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            Add your first provider
          </button>
        </div>
      ) : (
        <div className="row">
          {idps.map((idp) => {
            const style = getProviderStyle(idp.provider_type);
            return (
              <div key={idp.id} className="col-md-6 mB-20">
                <div className={`card h-100 ${!idp.is_active ? 'opacity-50' : ''}`}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mB-15">
                      <div className="d-flex align-items-center">
                        <i className={`${style.icon} ${style.color} fsz-lg mR-10`}></i>
                        <div>
                          <h6 className="mB-0">{idp.name}</h6>
                          <small className="text-muted">{idp.provider_type}</small>
                        </div>
                      </div>
                      <span className={`badge ${idp.is_active ? 'bg-success' : 'bg-secondary'}`}>
                        {idp.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div className="small text-muted mB-10">
                      <div><strong>Issuer:</strong> {idp.issuer_url}</div>
                      <div><strong>Key Source:</strong> {idp.key_source}
                        {idp.key_source === 'static' && ` (${idp.public_keys_count || 0} keys)`}
                      </div>
                      <div>
                        <strong>Client ID:</strong>{' '}
                        <code className="small">{idp.client_id || '—'}</code>
                      </div>
                      <div>
                        <strong>Client Secret:</strong>{' '}
                        <code className="small">{idp.client_secret || '—'}</code>
                      </div>
                      <div>
                        <strong>Applications:</strong>{' '}
                        <span className={appCounts[idp.id] === 0 ? 'text-muted' : ''}>
                          {appCounts[idp.id] !== undefined ? appCounts[idp.id] : '...'}
                        </span>
                      </div>
                      <div>
                        <strong>JIT Provisioning:</strong>{' '}
                        {idp.jit_provisioning_enabled ? (
                          <span className="text-success">Enabled</span>
                        ) : (
                          <span className="text-muted">Disabled</span>
                        )}
                        {idp.jit_provisioning_enabled && idp.default_role && (
                          <span> (Role: {idp.default_role})</span>
                        )}
                      </div>
                    </div>

                    <div className="d-flex gap-2 flex-wrap">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          setEditingIdp(idp);
                          setShowEditModal(true);
                        }}
                      >
                        <i className="ti-pencil mR-5"></i>Edit
                      </button>
                      {idp.key_source === 'static' && (
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => {
                            setKeyManagerIdp(idp);
                            setShowKeyModal(true);
                          }}
                        >
                          <i className="ti-key mR-5"></i>Keys
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(idp)}
                      >
                        <i className="ti-trash mR-5"></i>
                        {idp.is_active ? 'Deactivate' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add IDP Modal */}
      {showAddModal && (
        <AddIdpModal
          orgId={orgId}
          onClose={() => setShowAddModal(false)}
          onCreated={() => {
            setShowAddModal(false);
            setSuccess('Identity provider created successfully');
            setTimeout(() => setSuccess(''), 3000);
            loadIdps();
          }}
        />
      )}

      {/* Edit IDP Modal */}
      {showEditModal && editingIdp && (
        <EditIdpModal
          orgId={orgId}
          idp={editingIdp}
          onClose={() => {
            setShowEditModal(false);
            setEditingIdp(null);
          }}
          onUpdated={() => {
            setShowEditModal(false);
            setEditingIdp(null);
            setSuccess('Identity provider updated successfully');
            setTimeout(() => setSuccess(''), 3000);
            loadIdps();
          }}
        />
      )}

      {/* Key Manager Modal */}
      {showKeyModal && keyManagerIdp && (
        <StaticKeyManager
          orgId={orgId}
          idp={keyManagerIdp}
          onClose={() => {
            setShowKeyModal(false);
            setKeyManagerIdp(null);
          }}
          onUpdated={loadIdps}
        />
      )}
    </div>
  );
};

// Add IDP Modal Component
const AddIdpModal = ({ orgId, onClose, onCreated }) => {
  const [method, setMethod] = useState('discovery');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [issuerUrl, setIssuerUrl] = useState('');
  const [audience, setAudience] = useState('');
  const [providerType, setProviderType] = useState('auth0');
  const [jwksUri, setJwksUri] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (method === 'discovery') {
        await createIdpViaDiscovery(orgId, {
          name,
          issuer_url: issuerUrl,
          audience,
          client_id: clientId || undefined,
          client_secret: clientSecret || undefined,
        });
      } else {
        await createIdpManual(orgId, {
          name,
          provider_type: providerType,
          issuer_url: issuerUrl,
          audience,
          key_source: 'dynamic',
          jwks_uri: jwksUri || undefined,
          client_id: clientId || undefined,
          client_secret: clientSecret || undefined,
        });
      }
      onCreated();
    } catch (e) {
      console.error('Failed to create IDP:', e);
      setError(e.message || 'Failed to create identity provider');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Identity Provider</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger">{error}</div>
              )}

              <div className="mB-20">
                <label className="form-label fw-bold">Setup Method</label>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="method"
                    id="methodDiscovery"
                    checked={method === 'discovery'}
                    onChange={() => setMethod('discovery')}
                  />
                  <label className="form-check-label" htmlFor="methodDiscovery">
                    <strong>OIDC Discovery</strong>
                    <span className="badge bg-primary ms-2">Recommended</span>
                    <p className="text-muted small mB-0">Automatically discover settings from issuer URL</p>
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="method"
                    id="methodManual"
                    checked={method === 'manual'}
                    onChange={() => setMethod('manual')}
                  />
                  <label className="form-check-label" htmlFor="methodManual">
                    <strong>Manual Configuration</strong>
                    <p className="text-muted small mB-0">Enter all settings manually</p>
                  </label>
                </div>
              </div>

              <div className="mB-15">
                <label className="form-label" htmlFor="name">Display Name *</label>
                <input
                  type="text"
                  className="form-control"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Auth0 Production"
                  required
                />
              </div>

              {method === 'manual' && (
                <div className="mB-15">
                  <label className="form-label" htmlFor="providerType">Provider Type *</label>
                  <select
                    className="form-select"
                    id="providerType"
                    value={providerType}
                    onChange={(e) => setProviderType(e.target.value)}
                  >
                    <option value="auth0">Auth0</option>
                    <option value="okta">Okta</option>
                    <option value="azure_ad">Azure AD</option>
                    <option value="generic_oidc">Generic OIDC</option>
                  </select>
                </div>
              )}

              <div className="mB-15">
                <label className="form-label" htmlFor="issuerUrl">Issuer URL *</label>
                <input
                  type="url"
                  className="form-control"
                  id="issuerUrl"
                  value={issuerUrl}
                  onChange={(e) => setIssuerUrl(e.target.value)}
                  placeholder="https://mycompany.auth0.com/"
                  required
                />
                <div className="form-text">Include trailing slash. Example: https://tenant.auth0.com/</div>
              </div>

              <div className="mB-15">
                <label className="form-label" htmlFor="audience">Audience *</label>
                <input
                  type="text"
                  className="form-control"
                  id="audience"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="https://api.mycompany.com"
                  required
                />
                <div className="form-text">The API identifier from your Auth0 dashboard</div>
              </div>

              <div className="mB-15">
                <label className="form-label" htmlFor="clientId">Client ID</label>
                <input
                  type="text"
                  className="form-control"
                  id="clientId"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="your-client-id"
                />
                <div className="form-text">
                  {providerType === 'auth0' && 'Find in Auth0 Dashboard → Applications → Settings'}
                  {providerType === 'okta' && 'Find in Okta Admin → Applications → Your App'}
                  {providerType === 'azure_ad' && 'Find in Azure Portal → App Registrations → Overview'}
                  {providerType === 'generic_oidc' && 'OAuth client ID from your identity provider'}
                </div>
              </div>

              <div className="mB-15">
                <label className="form-label" htmlFor="clientSecret">Client Secret</label>
                <input
                  type="password"
                  className="form-control"
                  id="clientSecret"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder="Enter client secret"
                />
                <div className="form-text">OAuth client secret (stored securely)</div>
              </div>

              {method === 'manual' && (
                <div className="mB-15">
                  <label className="form-label" htmlFor="jwksUri">JWKS URI (optional)</label>
                  <input
                    type="url"
                    className="form-control"
                    id="jwksUri"
                    value={jwksUri}
                    onChange={(e) => setJwksUri(e.target.value)}
                    placeholder="https://mycompany.auth0.com/.well-known/jwks.json"
                  />
                  <div className="form-text">Leave blank to use default: {'{issuer}'}/.well-known/jwks.json</div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mR-5"></span>
                    Creating...
                  </>
                ) : method === 'discovery' ? (
                  'Discover & Create'
                ) : (
                  'Create'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Edit IDP Modal Component
const EditIdpModal = ({ orgId, idp, onClose, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState(idp.name || '');
  const [audience, setAudience] = useState(idp.audience || '');
  const [clientId, setClientId] = useState(idp.client_id || '');
  const [clientSecret, setClientSecret] = useState(''); // Don't pre-fill for security
  const [keySource, setKeySource] = useState(idp.key_source || 'dynamic');
  const [jwksUri, setJwksUri] = useState(idp.jwks_uri || '');
  const [isActive, setIsActive] = useState(idp.is_active !== false);

  // JIT Configuration state
  const [jitConfig, setJitConfig] = useState({
    jit_provisioning_enabled: idp.jit_provisioning_enabled || false,
    user_type_for_jit: idp.user_type_for_jit || 'client_user',
    claim_mappings: idp.claim_mappings || {},
    default_field_values: idp.default_field_values || {},
    default_role: idp.default_role || 'user',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        name,
        audience,
        client_id: clientId || undefined,
        key_source: keySource,
        jwks_uri: keySource === 'dynamic' ? jwksUri : undefined,
        is_active: isActive,
        // JIT configuration fields
        jit_provisioning_enabled: jitConfig.jit_provisioning_enabled,
        user_type_for_jit: jitConfig.jit_provisioning_enabled ? jitConfig.user_type_for_jit : undefined,
        claim_mappings: jitConfig.jit_provisioning_enabled ? jitConfig.claim_mappings : undefined,
        default_field_values: jitConfig.jit_provisioning_enabled ? jitConfig.default_field_values : undefined,
        default_role: jitConfig.jit_provisioning_enabled ? jitConfig.default_role : undefined,
      };
      // Only send client_secret if user entered a new one
      if (clientSecret) {
        payload.client_secret = clientSecret;
      }
      await updateIdpConfiguration(orgId, idp.id, payload);
      onUpdated();
    } catch (e) {
      console.error('Failed to update IDP:', e);
      setError(e.message || 'Failed to update identity provider');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Identity Provider</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger">{error}</div>
              )}

              <div className="row mB-15">
                <div className="col-md-6">
                  <label className="form-label">Display Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Provider Type</label>
                  <input
                    type="text"
                    className="form-control"
                    value={idp.provider_type}
                    disabled
                  />
                </div>
              </div>

              <div className="mB-15">
                <label className="form-label">Issuer URL</label>
                <input
                  type="text"
                  className="form-control"
                  value={idp.issuer_url}
                  disabled
                />
                <div className="form-text">Issuer URL cannot be changed after creation</div>
              </div>

              <div className="mB-15">
                <label className="form-label">Audience *</label>
                <input
                  type="text"
                  className="form-control"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  required
                />
              </div>

              <div className="row mB-15">
                <div className="col-md-6">
                  <label className="form-label">Client ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="your-client-id"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Client Secret</label>
                  <input
                    type="password"
                    className="form-control"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder={idp.client_secret ? 'Leave blank to keep existing' : 'Enter secret'}
                  />
                  {idp.client_secret && (
                    <div className="form-text">
                      Current: <code>{idp.client_secret}</code>
                    </div>
                  )}
                </div>
              </div>

              <div className="mB-15">
                <label className="form-label fw-bold">Key Source</label>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="keySource"
                    id="keySourceDynamic"
                    checked={keySource === 'dynamic'}
                    onChange={() => setKeySource('dynamic')}
                  />
                  <label className="form-check-label" htmlFor="keySourceDynamic">
                    <strong>Dynamic (JWKS)</strong> - Fetch keys automatically
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="keySource"
                    id="keySourceStatic"
                    checked={keySource === 'static'}
                    onChange={() => setKeySource('static')}
                  />
                  <label className="form-check-label" htmlFor="keySourceStatic">
                    <strong>Static</strong> - Use uploaded public keys
                  </label>
                </div>
              </div>

              {keySource === 'dynamic' && (
                <div className="mB-15">
                  <label className="form-label">JWKS URI</label>
                  <input
                    type="url"
                    className="form-control"
                    value={jwksUri}
                    onChange={(e) => setJwksUri(e.target.value)}
                    placeholder="Auto-discovered"
                  />
                </div>
              )}

              <hr />

              <h6>User Provisioning</h6>
              <JitConfigForm config={jitConfig} onChange={setJitConfig} />

              <hr />

              <h6>Status</h6>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="isActive"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="isActive">
                  Active
                </label>
                <p className="text-muted small mB-0">Deactivating will prevent all authentication via this IDP</p>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mR-5"></span>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Static Key Manager Component
const StaticKeyManager = ({ orgId, idp, onClose, onUpdated }) => {
  const [keyFormat, setKeyFormat] = useState('jwk');
  const [keyData, setKeyData] = useState('');
  const [kid, setKid] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');
    setSuccess('');

    try {
      // Validate JWK format
      if (keyFormat === 'jwk') {
        try {
          JSON.parse(keyData);
        } catch {
          throw new Error('Invalid JSON format for JWK');
        }
      }

      // Validate PEM has kid
      if (keyFormat === 'pem' && !kid.trim()) {
        throw new Error('Key ID is required for PEM format');
      }

      await uploadPublicKey(orgId, idp.id, {
        key_data: keyData,
        key_format: keyFormat,
        kid: keyFormat === 'pem' ? kid : undefined,
      });

      setKeyData('');
      setKid('');
      setSuccess('Key uploaded successfully');
      setTimeout(() => setSuccess(''), 3000);
      onUpdated();
    } catch (e) {
      setError(e.message || 'Failed to upload key');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Manage Public Keys - {idp.name}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {error && (
              <div className="alert alert-danger alert-dismissible fade show">
                {error}
                <button type="button" className="btn-close" onClick={() => setError('')}></button>
              </div>
            )}

            {success && (
              <div className="alert alert-success alert-dismissible fade show">
                {success}
                <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
              </div>
            )}

            <div className="alert alert-info">
              <i className="ti-info-alt mR-10"></i>
              <strong>Key Source:</strong> {idp.key_source}
              {idp.key_source === 'static' && (
                <span> - Static keys require manual rotation.</span>
              )}
            </div>

            <h6>Current Keys ({idp.public_keys_count || 0})</h6>
            {idp.public_keys_count === 0 ? (
              <p className="text-muted">No keys uploaded yet.</p>
            ) : (
              <p className="text-muted">Keys are stored securely. Use the form below to add new keys.</p>
            )}

            <hr />

            <h6>Add New Key</h6>
            <form onSubmit={handleUpload}>
              <div className="mB-15">
                <label className="form-label fw-bold">Key Format</label>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="keyFormat"
                    id="keyFormatJwk"
                    checked={keyFormat === 'jwk'}
                    onChange={() => setKeyFormat('jwk')}
                  />
                  <label className="form-check-label" htmlFor="keyFormatJwk">
                    JWK (JSON Web Key)
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="keyFormat"
                    id="keyFormatPem"
                    checked={keyFormat === 'pem'}
                    onChange={() => setKeyFormat('pem')}
                  />
                  <label className="form-check-label" htmlFor="keyFormatPem">
                    PEM (Public Key)
                  </label>
                </div>
              </div>

              <div className="mB-15">
                <label className="form-label">Key Data *</label>
                <textarea
                  className="form-control font-monospace"
                  value={keyData}
                  onChange={(e) => setKeyData(e.target.value)}
                  placeholder={keyFormat === 'jwk'
                    ? '{"kty":"RSA","n":"...","e":"AQAB","kid":"key-1"}'
                    : '-----BEGIN PUBLIC KEY-----\n...\n-----END PUBLIC KEY-----'
                  }
                  rows={6}
                  required
                />
              </div>

              {keyFormat === 'pem' && (
                <div className="mB-15">
                  <label className="form-label">Key ID (kid) *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={kid}
                    onChange={(e) => setKid(e.target.value)}
                    placeholder="key-2024-01"
                    required
                  />
                  <div className="form-text">Must match the 'kid' header in tokens signed with this key</div>
                </div>
              )}

              <button type="submit" className="btn btn-primary" disabled={uploading}>
                {uploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mR-5"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="ti-upload mR-5"></i>
                    Upload Key
                  </>
                )}
              </button>
            </form>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdpConfigList;
